<script lang="ts" setup>
  import { computed, ref, useTemplateRef, watch } from 'vue'

  import VueMarkdown from 'vue-markdown-render'

  import { useTheme } from 'vuetify'

  import { useFirebaseApp, useFirebaseStorage } from 'vuefire'

  import { getFunctions, httpsCallable } from 'firebase/functions'
  import { getBlob, ref as storageRef } from 'firebase/storage'

  import useOuraAuth from '#web-oura-tools/auth'

  import { useServiceLaunch } from '#ourahub-intra/composables/serviceLaunch'

  import { useAppStore } from '#ourahub-intra/stores/application'
  import { useServicesStore } from '#ourahub-intra/stores/services'

  import { isOcpAppNotRunning, useOcpStatusQuery } from '#ourahub-intra/queries/ocp'

  import { STAGE_VERSION_NAMES, buildServiceUrl } from '#ourahub-intra/constants'

  import { gcpRegion } from '#vue-shared-lib/utils/cloud-env'

  import type { Category, Service, ServiceDetailsSection } from '#ourahub-intra/types/services'
  import type { AppEnvironment } from '#web-oura-tools/types/auth'

  // Either pass an `id` to resolve the service / category from the store, or pass them directly.
  type ServiceDetailsProps = {
    // Forces the app / tool type dropdown open (for intro tour).
    openTypeMenu?: boolean
    focusSection?: ServiceDetailsSection
  } & ({ id: string; service?: never; category?: never } | { id?: never; service: Service; category: Category })

  const props = defineProps<ServiceDetailsProps>()

  const view = defineModel<'info' | 'settings'>('view', { default: 'info' })

  const emits = defineEmits<{
    close: []
  }>()

  const changelog = ref('')
  const dialogReady = ref(false)
  const versionsSection = useTemplateRef<HTMLElement>('versionsSection')
  const maintainersSection = useTemplateRef<HTMLElement>('maintainersSection')
  const developmentSection = useTemplateRef<HTMLElement>('developmentSection')
  const changelogSection = useTemplateRef<HTMLElement>('changelogSection')
  const permissionsSection = useTemplateRef<HTMLElement>('permissionsSection')
  const contactSection = useTemplateRef<HTMLElement>('contactSection')
  const statusSection = useTemplateRef<HTMLElement>('statusSection')

  const isSettingsOpen = computed(() => view.value === 'settings')

  const isPublishingFocused = ref(false)

  const theme = useTheme()

  const { launchService } = useServiceLaunch()

  const ouraAuth = useOuraAuth()

  const fbStorage = useFirebaseStorage()

  const firebaseApp = useFirebaseApp()

  const ouraHubStore = useAppStore()
  const servicesStore = useServicesStore()

  const downloadState = ref<'idle' | 'downloading' | 'done' | 'error'>('idle')

  const downloadLabel = computed(
    () =>
      ({ idle: 'Download code', downloading: 'Download code', done: 'Code downloaded', error: 'Download failed' })[
        downloadState.value
      ],
  )

  const downloadIcon = computed(
    () =>
      ({ idle: 'mdi-download', downloading: 'mdi-download', done: 'mdi-check', error: 'mdi-alert' })[
        downloadState.value
      ],
  )

  const showPublishDialog = ref(false)

  const publishStage = ref<AppEnvironment>(ouraAuth.appEnv.value)

  const service = computed(() => props.service || servicesStore.getService(props.id!)!)
  const category = computed(() => props.category || servicesStore.getCategory(props.id!))

  const focusedSection = computed(() =>
    props.focusSection === 'maintainers' &&
    !servicesStore.publicStage(service.value) &&
    servicesStore.isMaintainer(service.value)
      ? 'development'
      : props.focusSection,
  )

  const focusedElement = computed(() => {
    switch (focusedSection.value) {
      case 'versions':
        return versionsSection.value
      case 'maintainers':
        return maintainersSection.value
      case 'development':
        return developmentSection.value
      case 'changelog':
        return changelogSection.value
      case 'permissions':
        return permissionsSection.value
      case 'contact':
        return contactSection.value
      case 'status':
        return statusSection.value
      default:
        return null
    }
  })

  watch(
    [dialogReady, focusedElement, view],
    () => {
      if (!dialogReady.value || view.value !== 'info') return

      focusedElement.value?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        block: 'start',
      })
    },
    { flush: 'post' },
  )

  const { data: statusData } = useOcpStatusQuery(service.value.id)

  const errorState = computed(() => {
    if (!isOcpAppNotRunning(statusData.value)) {
      return false
    }

    const appStatus = statusData?.value?.resources.find((r) => r.kind === 'OuraHubApp')

    return appStatus?.workloads?.map((w) => w.state).join(' / ')
  })

  const appVersionInfo = computed(() =>
    !service.value?.public
      ? 'There is no public version released yet, only collaborators can access!'
      : service.value?.public === 'sandbox'
        ? `Current development version is ${service.value?.version} which can be under development and may be unstable.`
        : service.value?.public === 'staging'
          ? `Current experimental version is ${service.value?.version} and may have unstable features, please report all issues.`
          : `Current production version is ${service.value?.version} and is considered to be stable and safe for daily use.`,
  )

  // Outside the loaded app, describe the version its catalog card would launch
  const viewStage = computed(
    () =>
      (ouraHubStore.activeService !== service.value.id &&
        (servicesStore.publicStage(service.value) ?? servicesStore.launchStages(service.value).at(-1))) ||
      ouraAuth.appEnv.value,
  )

  const accessStatus = computed(() => servicesStore.accessStatus(service.value, viewStage.value))

  // Sorts docs so children appear after their parent, and computes each item's nesting depth.
  // Depth is determined by counting ancestor routes present in the list (exact segment match).
  const sortedDocs = computed(() => {
    if (!service.value.docs) {
      return []
    }

    const normalize = (route: string) => (route.startsWith('/') ? route : '/' + route)

    const sorted = [...service.value.docs].sort((a, b) => normalize(a.route).localeCompare(normalize(b.route)))

    const routeSet = new Set(sorted.map((d) => normalize(d.route)))

    return sorted.map((doc) => {
      const segments = normalize(doc.route).split('/').filter(Boolean)
      let depth = 0

      for (let i = segments.length - 1; i > 0; i--) {
        if (routeSet.has('/' + segments.slice(0, i).join('/'))) {
          depth++
        }
      }

      return { doc, depth }
    })
  })

  function openSettings(focusPublishing = false) {
    isPublishingFocused.value = focusPublishing
    view.value = 'settings'
  }

  function closeSettings() {
    view.value = 'info'
  }

  async function openPublishedVersion(stage: AppEnvironment) {
    await launchService(service.value, stage)

    if (!servicesStore.isLaunchableInNewTab(service.value)) {
      emits('close')
    }
  }

  async function downloadCode() {
    const repo = service.value.github
      ?.split('/')
      .pop()
      ?.replace(/\.git$/, '')

    if (!repo) {
      return
    }

    try {
      downloadState.value = 'downloading'

      const { data } = await httpsCallable<{ repo: string }, { downloadUrl: string }>(
        getFunctions(firebaseApp, gcpRegion),
        'downloadRepositoryFromGitHub',
      )({ repo })

      const link = document.createElement('a')
      link.href = data.downloadUrl
      link.download = ''
      link.click()

      downloadState.value = 'done'
    } catch (e: unknown) {
      console.error(e)
      downloadState.value = 'error'
    }
  }

  function openPublishDialog(stage: AppEnvironment) {
    publishStage.value = stage
    showPublishDialog.value = true
  }

  watch(
    () => service.value,
    async (curService) => {
      changelog.value = curService
        ? await getBlob(storageRef(fbStorage, `changelogs/${curService.id}.md`))
            .then((blob) => blob?.text() ?? '')
            .catch(() => '')
        : ''
    },
    { immediate: true },
  )
</script>

<template lang="html">
  <v-dialog
    scrollable
    width="800"
    height="600"
    max-height="600"
    :model-value="true"
    @after-enter="dialogReady = true"
    @update:model-value="!$event ? emits('close') : undefined"
  >
    <v-card :data-intro-tour-id="isSettingsOpen ? 'settings-dialog' : 'info-dialog'">
      <v-card-item>
        <v-card-subtitle>{{ (category?.name ?? service.category).toUpperCase() }}</v-card-subtitle>

        <v-row>
          <v-icon size="large" :icon="service.icon" :color="category?.color ?? 'primary'" />

          <v-card-title class="ml-n4">{{ service.name }}</v-card-title>
        </v-row>

        <template #append v-if="!isSettingsOpen">
          <o-btn-secondary
            v-if="servicesStore.isMaintainer(service)"
            text="Edit settings"
            append-icon="mdi-cog"
            data-intro-tour-id="edit-settings"
            @click.stop="openSettings()"
          />

          <o-btn-text
            active
            size="small"
            icon="mdi-pin"
            class="ml-2 mr-n2 mt-n1 mb-n1"
            :class="
              servicesStore.isPinnedService(service.id)
                ? 'text-deep-orange'
                : theme.global.name.value === 'dark'
                  ? 'text-blue-grey-lighten-3'
                  : 'text-blue-grey-darken-3'
            "
            @click.stop="servicesStore.togglePinnedService(service.id)"
          />
        </template>
      </v-card-item>

      <v-divider />

      <ServiceSettings
        v-if="isSettingsOpen"
        :service="service"
        :open-type-menu="openTypeMenu"
        :scroll-to-publishing="isPublishingFocused"
        @close="closeSettings()"
        @publish="openPublishDialog($event)"
      />

      <template v-else>
        <v-card-text>
          <o-alert
            v-if="accessStatus.reason && service.id !== 'outo-ourahub-intra'"
            density="compact"
            class="mt-n2 mx-n4"
            :color="accessStatus.color"
          >
            {{ accessStatus.reason }}

            <template #append>
              <o-btn-primary
                v-if="accessStatus.possibleAction === 'publish-settings'"
                class="mt-2"
                text="Version settings"
                @click="openSettings(true)"
              />

              <o-btn-primary
                v-if="accessStatus.possibleAction === 'open-published-version'"
                class="mt-2"
                :text="`Open ${STAGE_VERSION_NAMES[accessStatus.publishedVersion!]} version`"
                @click="openPublishedVersion(accessStatus.publishedVersion!)"
              />

              <o-btn-primary
                v-if="accessStatus.possibleAction === 'request-access-rights'"
                class="px-4"
                target="_blank"
                text="Request access rights"
                href="https://oura.service-now.com/sp"
              />
            </template>
          </o-alert>

          <h2 class="text-title-large">Description</h2>

          <p>{{ service.description }}</p>

          <p v-if="category">{{ category.info }}</p>

          <p v-if="service.context === 'web-service-ssr'">
            This is an external / standalone web app which can not be loaded inside OuraHub and will always be opened in
            new window.
          </p>

          <section
            ref="contactSection"
            aria-label="Contact info"
            :class="{ 'section-highlight': focusedSection === 'contact' }"
          >
            <h2 class="text-title-large">Contact info</h2>
            <p>
              If you run into any issues or have questions that the top bar help won't answer, please send a message to
              <b>{{ service.contact || '#help-oura-tooling' }}</b>
              in Slack.
            </p>
          </section>

          <section
            ref="statusSection"
            aria-label="Status"
            :class="{ 'section-highlight': focusedSection === 'status' }"
          >
            <h2 class="text-title-large">Status</h2>

            <p v-if="errorState">Cloud service is currently not running, reported state: {{ errorState }}.</p>
            <p v-else>Cloud service should be operating normally.</p>
          </section>

          <section
            v-if="service.entry"
            ref="versionsSection"
            aria-label="Versions"
            :class="{ 'section-highlight': focusedSection === 'versions' }"
          >
            <h2 class="text-title-large">Versions</h2>

            <p>Deployments available in the following versions / environments:</p>

            <o-chip
              v-if="service.entry?.sandbox"
              color="green"
              class="mt-2 mr-2"
              text="Development / Sandbox"
              :variant="viewStage === 'sandbox' ? 'flat' : 'tonal'"
              :class="viewStage === 'sandbox' ? 'font-weight-bold' : ''"
            />
            <o-chip
              v-if="service.entry?.staging"
              color="purple"
              class="mt-2 mr-2"
              text="Experimental / Staging"
              :variant="viewStage === 'staging' ? 'flat' : 'tonal'"
              :class="viewStage === 'staging' ? 'font-weight-bold' : ''"
            />
            <o-chip
              v-if="service.entry?.release"
              color="black"
              class="mt-2 mt-2 mr-2"
              text="Production / Release"
              :variant="viewStage === 'release' ? 'flat' : 'tonal'"
              :class="viewStage === 'release' ? 'font-weight-bold' : ''"
            />

            <br />

            <p>
              {{ appVersionInfo }}
            </p>
          </section>

          <section
            ref="permissionsSection"
            aria-label="Permissions"
            :class="{ 'section-highlight': focusedSection === 'permissions' }"
          >
            <h2 class="text-title-large">Permissions</h2>

            <p v-if="service.rights?.[viewStage]">
              Following permission is required to be able to access:
              <br />

              <o-chip
                class="mt-2 mr-2"
                :text="service.rights[viewStage].replace(/ Users$/, ' Access')"
                :color="servicesStore.hasAccessRights(service, viewStage) ? 'success' : 'warning'"
              />
            </p>
            <p v-else>No special permission required to access, but some features may require them.</p>
          </section>

          <section
            v-if="servicesStore.isMaintainer(service)"
            ref="developmentSection"
            aria-label="Development"
            :class="{ 'section-highlight': focusedSection === 'development' }"
          >
            <h2 class="text-title-large">Development</h2>

            <a
              target="_blank"
              class="text-info"
              rel="noopener noreferrer"
              :href="buildServiceUrl(service.id, 'sandbox')"
            >
              Direct URL
            </a>

            <span class="mx-2">|</span>

            <a
              v-if="service.github"
              target="_blank"
              class="text-info"
              :href="service.github?.replace('git@github.com:', 'https://github.com/').replace('.git', '')"
            >
              Code repository
            </a>

            <span class="mx-2">|</span>

            <a
              target="_blank"
              class="text-info"
              :href="`${service.github?.replace('git@github.com:', 'https://github.com/').replace('.git', '')}/actions/workflows/deploy.yml`"
            >
              Deployment logs
            </a>

            <span class="mx-2">|</span>

            <a
              target="_blank"
              class="text-info"
              :href="`https://monitoring.oura.cloud/explore?schemaVersion=1&panes=%7B%225yb%22:%7B%22datasource%22:%22loki%22,%22queries%22:%5B%7B%22refId%22:%22A%22,%22expr%22:%22%7Bcluster%3D%5C%22ourahub-sbox-euc1%5C%22,%20namespace%3D%5C%22ourahub-${service.id}%5C%22%7D%20%7C%3D%20%60%60%22,%22queryType%22:%22range%22,%22datasource%22:%7B%22type%22:%22loki%22,%22uid%22:%22loki%22%7D,%22editorMode%22:%22builder%22,%22direction%22:%22backward%22%7D%5D,%22range%22:%7B%22from%22:%22now-1h%22,%22to%22:%22now%22%7D,%22panelsState%22:%7B%22logs%22:%7B%22sortOrder%22:%22Descending%22%7D%7D,%22compact%22:false%7D%7D&orgId=1`"
            >
              Runtime server logs
            </a>

            <div v-if="service.proto && service.github" class="mt-3">
              <o-btn-secondary
                :text="downloadLabel"
                :prepend-icon="downloadIcon"
                :loading="downloadState === 'downloading'"
                :color="downloadState === 'error' ? 'error' : undefined"
                @click="downloadCode()"
              />
            </div>
          </section>

          <section
            ref="maintainersSection"
            aria-label="Maintainers"
            data-intro-tour-id="maintainers"
            :class="{ 'section-highlight': focusedSection === 'maintainers' }"
          >
            <h2 class="text-title-large">Maintainers</h2>

            <div class="mt-2 mb-4 d-flex flex-wrap align-center ga-2">
              <o-chip v-if="service.owner" :text="service.owner">
                <template #prepend>
                  <v-tooltip text="Owner" location="top">
                    <template #activator="{ props: tooltipProps }">
                      <v-icon v-bind="tooltipProps" class="mr-1" icon="mdi-crown" />
                    </template>
                  </v-tooltip>
                </template>
              </o-chip>

              <o-chip v-for="editor in service.editors ?? []" :key="editor" :text="editor" />

              <div v-if="!service.owner && !service.editors?.length" class="text-medium-emphasis">
                No maintainers assigned.
              </div>
              <div v-else class="mt-2">
                You can contact the maintainers to get access to the development versions of the app if needed.
              </div>
            </div>
          </section>

          <template v-if="service.docs?.length">
            <h2 class="text-title-large">Documentation</h2>

            <ul>
              <li v-for="{ doc, depth } in sortedDocs" :key="doc.route" :style="'margin-left: ' + depth * 20 + 'px'">
                <a target="_blank" class="text-info" rel="noopener noreferrer" :href="doc.url">{{ doc.title }}</a>
              </li>
            </ul>
          </template>

          <!--
        // This will be added in the future

        <h2 class="text-title-large">Integrations</h2>

        <p v-if="!service.apis?.length" class="text-medium-emphasis">
          This app does not have any known integrations yet.
        </p>


        <template v-else>
          <p>
            This app connects to the following systems:
            <br />
          </p>

          <ul>
            <li v-for="(integration, i) in service.apis ?? []" :key="i" class="mb-2">
              {{ integration.name }}:

              <o-chip
                v-for="(operation, oi) in integration.permissions?.split(',') ?? []"
                :key="oi"
                class="mr-2"
                :text="operation"
              />
            </li>
          </ul>
        </template>
              -->

          <h2 class="text-title-large">Analytics</h2>

          <ServiceStatistics :service-id="service.id" :color="category?.color ?? 'primary'" />

          <section
            v-if="!!changelog"
            ref="changelogSection"
            aria-label="Release history"
            :class="{ 'section-highlight': focusedSection === 'changelog' }"
          >
            <h2 class="text-title-large">Release history</h2>

            <vue-markdown class="changelog" :source="changelog" />
          </section>
        </v-card-text>

        <v-card-actions>
          <o-btn-primary text="Close" class="px-4 mr-2" @click="emits('close')" />
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>

  <ServicePublish v-model="showPublishDialog" :service="service" :stage="publishStage" />
</template>

<style lang="scss" scoped>
  section {
    scroll-margin-top: 12px;
  }

  .section-highlight {
    margin: 16px -12px;
    outline: 2px solid rgb(var(--v-theme-info));
    border-radius: 4px;
    background: rgba(var(--v-theme-info), 0.08);
    padding: 2px 12px 12px 12px;
  }

  :deep(.changelog) {
    margin-top: -12px;
    padding-left: 20px;

    h1 {
      display: none;
    }

    h2 {
      opacity: 0.8;
      margin-bottom: 4px;
      border-bottom: 1px solid lightgrey;
      padding: 12px 0 8px 0;
      color: rgb(var(--v-theme-foreground));
      font-weight: normal;
      font-size: 1.2em;

      a {
        color: rgb(var(--v-theme-foreground));
        font-weight: normal;
        text-decoration: unset;
      }
    }

    h3 {
      padding: 4px 0 0 0;
      color: rgb(var(--v-theme-foreground));
      font-weight: normal;
    }

    ul {
      color: rgb(var(--v-theme-foreground)) !important;

      a {
        color: rgb(var(--v-theme-info));
        font-weight: bold;
        text-decoration: unset;
      }

      li:first-letter {
        text-transform: uppercase;
      }
    }
  }
</style>
